import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import {
  Hyperlink,
  INLINES,
  EntryHyperlink,
  Node,
  AssetHyperlink,
} from '@contentful/rich-text-types'
import { EntryFields, EntriesQueries, EntrySkeletonType } from 'contentful'
import striptags from 'striptags'
import { ref } from 'vue'

import { unEscapeString } from '@/lib/utils/strings'

import { useContentful } from '@/components/common/contentful/composables/useContentful'

import {
  ContentfulEntry,
  ContentfulPage,
  ContentfulResource,
  ContentfulPdf,
  ContentfulPageFields,
} from '@/types'

export function useContentfulComponent() {
  const contentful = useContentful()
  const item = ref<object | null>(null)
  const contentfulState = ref({
    error: false,
    loading: true,
  })

  function initData<T extends object>(entry: T) {
    item.value = { ...entry }
  }

  function getPageBySlug(slug: string = '') {
    return contentful.service.getPageBySlug(slug)
  }

  function getSiteCopy(copyGroupId: string, copyItemId: string, stripTags = true) {
    const copyItem = contentful.service.getSiteCopy(copyGroupId, copyItemId)
    if (!copyItem) return ''
    return richToHtml(copyItem, stripTags)
  }

  function richToHtml(richData: EntryFields.RichText, stripTags = true) {
    const getFields = <T extends ContentfulEntry>(node: Node): T['fields'] =>
      (node.data.target as unknown as T).fields

    const typeHandlers = {
      page: (node: EntryHyperlink) => {
        const fields = getFields<ContentfulPage>(node)
        const slug = fields?.slug
        return `<router-link
              key='${fields?.slug}'
              to='${typeof slug === 'string' ? getPageBySlug(slug)?.link : ''}'
            >${node.content[0].value}</router-link>`
      },
      resource: (node: EntryHyperlink) => {
        const item = getFields<ContentfulResource>(node)
        if (!item) return null
        let { wrappedSlug } = getSlugsForEducationalResource(item.resourceGroup?.fields, item.title)
        const isBlog = item?.resourceGroup?.fields?.slug === 'blogs'
        if (isBlog) wrappedSlug = !item.link ? `blogs/${item.slug}` : item.link
        const title = node.content[0]?.value || item.title
        return `<router-link
                  key='${item.slug}'
                  to='/${wrappedSlug}'
                >${title}</router-link>`
      },
      pdf: (node: AssetHyperlink) => {
        const fields = getFields<ContentfulPdf>(node)
        return `<a href="${fields.file.url}" target="_blank">${
          node.content[0] ? node.content[0].value : fields.title
        }</a>`
      },
      link: (node: Hyperlink) => {
        const domains = ['https://bioivt.com', 'https://www.bioivt.com']
        const target = domains.some((domain) => node.data.uri.startsWith(domain))
          ? '_self'
          : '_blank'
        return `<a href='${node.data.uri}' target="${target}">${node.content[0]?.value}</a>`
      },
    }
    const options = {
      renderNode: {
        [INLINES.ENTRY_HYPERLINK]: (node: EntryHyperlink) => {
          type HandlerType = keyof typeof typeHandlers
          const entry = node.data.target as unknown as ContentfulEntry
          const typeId = getContentTypeId(entry) as HandlerType
          if (typeId && typeHandlers[typeId]) return typeHandlers[typeId](node as never) // @TODO fix types
        },
        [INLINES.HYPERLINK]: (node: Hyperlink) => typeHandlers.link(node),
      },
    }
    const tagsToIgnore = ['router-link', 'a', 'i', 'b', 'strong']
    // @ts-expect-error @TODO fix types
    const htmlString = richData ? unEscapeString(documentToHtmlString(richData, options)) : ''
    return stripTags ? `${striptags(htmlString, tagsToIgnore)}` : htmlString
  }

  function getContentTypeId<T extends ContentfulEntry>(
    item: T | T[],
  ): T['sys']['contentType']['sys']['id'] {
    if (Array.isArray(item)) {
      const firstItem = item[0]
      if (!firstItem) return ''
      return 'sys' in firstItem && 'contentType' in firstItem.sys
        ? firstItem.sys.contentType?.sys.id || ''
        : ''
    } else {
      return 'sys' in item && 'contentType' in item.sys ? item.sys.contentType?.sys.id || '' : ''
    }
  }

  function renderScript(snippet: string) {
    if (snippet) {
      return `<VueScriptComponent script='${snippet.replace(/'/g, '"')}'/>`
    }
  }

  async function fetchItems<T extends EntrySkeletonType>(
    query: EntriesQueries<T, 'WITHOUT_LINK_RESOLUTION'>,
  ) {
    try {
      const response = await contentful.client.getEntries<T>(query)
      return response.items
    } catch (error) {
      contentfulState.value.error = true
      throw error
    } finally {
      contentfulState.value.loading = false
    }
  }

  function getSlugsForEducationalResource(
    parentPage: ContentfulPageFields | { pageType: string; title: string; slug: string },
    resourseTitle: string,
    objectID: string = '',
  ) {
    let slug, wrappedSlug, parentSlug
    if (contentful.service.isEducationalContent(parentPage?.pageType)) {
      slug = contentful.service.getEducationalContentPage()?.fields.slug || ''
      wrappedSlug = `${slug}?string=${encodeURIComponent(
        resourseTitle,
      )}&facets=(resourceGroup:"${encodeURIComponent(parentPage.title)}")`
      parentSlug = `${slug}?facets=(resourceGroup:"${encodeURIComponent(parentPage.title)}")`
    } else {
      slug = parentPage.slug === 'faq' ? 'resources/faq' : parentPage.slug || ''
      const entryName = parentPage.slug === 'faq' ? objectID : resourseTitle
      wrappedSlug = `${slug}?entry=${encodeURIComponent(entryName)}`
      parentSlug = slug
    }
    return {
      slug,
      wrappedSlug,
      parentSlug,
    }
  }

  return {
    item,
    contentfulState,
    initData,
    getPageBySlug,
    getSiteCopy,
    richToHtml,
    getImageUrl: contentful.service.getImageUrl,
    getImageAlt: contentful.service.getImageAlt,
    getContentTypeId,
    renderScript,
    fetchItems,
    getSlugsForEducationalResource,
  }
}
